import { Settings } from "lucide-react";
import { Link } from "react-router-dom";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { moduleLandingPath, navCallerOf } from "@/lib/module-registry";
import { ADMIN_MODULE } from "@/lib/modules";
import { useAuthSession } from "@/lib/session-context";

/**
 * Persistent entry point into the Admin app — users, permission sets,
 * segments, feature flags, and the shared catalogs.
 *
 * Was `GlobalEntryButton`, which pointed at `/accounts`. That made the gear
 * lie: a gear means configuration, but Accounts is a *record* list, and the
 * old "Global" area mixed the two. Records are shared nav modules inside
 * every module now (`lib/nav-records.tsx`), so the gear points where a gear
 * should — Admin.
 *
 * Deliberately just an icon + link, not a menu enumerating every Admin page:
 * that list only grows, and Admin's own sidebar already scales (sections,
 * icons-only collapse, scrolling). This button's job is to get the user into
 * that sidebar once — the way Google Cloud Console, Azure Portal, and GitHub
 * org settings all put admin behind a persistent header icon.
 *
 * Points where entering Admin lands this caller (`moduleLandingPath`):
 * Users, or their first Admin page when they can't open Users. Renders
 * nothing for a caller who can't open Admin (no `admin.access`) or any page
 * in it, so the gear never leads to a page that bounces. Gated here rather
 * than at each call site (topbar, launcher) so a new placement can't forget
 * it.
 */
export function AdminEntryButton() {
  const session = useAuthSession();
  const landing = moduleLandingPath(ADMIN_MODULE, navCallerOf(session));
  if (!landing) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={landing}
          aria-label="Admin — users, permissions, and settings"
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-row-hover hover:text-foreground"
        >
          <Settings size={17} strokeWidth={1.75} />
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom">Admin — users &amp; settings</TooltipContent>
    </Tooltip>
  );
}
